import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { ICustomerModel, ISalesPipelineModel, ISalesPipelineStageModel, ISalesTicketDocumentModel, ISalesTicketModel, SALES_PIPELINE_STAGE_KEYS, SALES_PIPELINE_STAGE_TYPES } from "@models";
import { GlobalsService, SalesPipelinesService, SalesTicketsService } from "@services";
import { JobsAuthService } from "@services/utils/auth-services/jobs-auth.service";
import { InvoicesAuthService } from "@services/utils/auth-services/invoices-auth.service";
import { EstimatesAuthService } from "@services/utils/auth-services/estimates-auth.service";
import { PaymentsAuthService } from "@services/utils/auth-services/payments-auth.service";

type StepState = 'done' | 'current' | 'todo';

// One node on the timeline: a column of the ticket's pipeline.
interface ProjectStep {
    stageId: number;
    name: string;
    key: string;
    sortOrder: number;
    color: string;
    icon: string;
    state: StepState;
    date: Date;
}

// One line in Related Documents. A step with several documents (two quotes, say) gets a row each;
// a step nothing has reached yet gets a single empty row so the full path is still visible.
interface ProjectDocumentRow {
    stepName: string;
    icon: string;
    state: StepState;
    date: Date;
    activity: string;
    documentLabel: string;
    documentType: 'ticket' | 'quote' | 'order' | 'job' | 'invoice';
    documentId: number;
    first: boolean;
}

interface NextStep {
    text: string;
    buttonLabel: string;
    buttonIcon: string;
    action: () => void;
}

/**
 * The Project Progress card on the customer overview.
 *
 * A "project" is a Sales Pipeline ticket: the one record that ties a lead, its quote, the Latham
 * order, the job and the invoice(s) for a piece of work together. The timeline is the ticket's
 * pipeline (its columns are per-company configuration, so they're read rather than hard-coded), the
 * position is the column the ticket sits in, and Related Documents are the records linked to it.
 *
 * Only open tickets are shown; a customer with none gets no card at all.
 */
@Component({
    selector: "customer-project-progress",
    templateUrl: "customer-project-progress.component.html",
    styleUrls: ["customer-project-progress.component.scss"],
    providers: [SalesTicketsService, SalesPipelinesService]
})
export class CustomerProjectProgressComponent implements OnChanges {
    @Input() customer: ICustomerModel;

    // Every create/open is handled by the customer page, which owns the dialogs. The number is the
    // ticket (for creates) or the document (for opens).
    @Output() createJob = new EventEmitter<number>();
    @Output() createQuote = new EventEmitter<number>();
    @Output() createInvoice = new EventEmitter<number>();
    @Output() createPayment = new EventEmitter<void>();
    @Output() openJob = new EventEmitter<number>();
    @Output() openQuote = new EventEmitter<number>();
    @Output() openInvoice = new EventEmitter<number>();
    @Output() openLathamOrder = new EventEmitter<number>();

    tickets: ISalesTicketModel[] = [];
    selectedTicket: ISalesTicketModel = null;
    steps: ProjectStep[] = [];
    rows: ProjectDocumentRow[] = [];
    currentStep: ProjectStep = null;
    nextStep: NextStep = null;
    progressPercent = 0;
    loading = false;

    canOpenBoard = GlobalsService.checkPermission("SalesPipeline", "Access");

    // Donut geometry: r=52 gives a circumference of ~326.7
    readonly ringCircumference = 2 * Math.PI * 52;

    private pipelinesById = new Map<number, ISalesPipelineModel>();

    // Auth services resolve from the customer page's own providers
    constructor(private salesTicketsService: SalesTicketsService,
        private salesPipelinesService: SalesPipelinesService,
        private jobsAuthService: JobsAuthService,
        private invoicesAuthService: InvoicesAuthService,
        private estimatesAuthService: EstimatesAuthService,
        private paymentsAuthService: PaymentsAuthService,
        private router: Router) {
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.customer)
            await this.reload();
    }

    // Re-read the tickets, keeping the one being looked at selected if it's still open. The customer
    // page calls this after a record is saved in a dialog, since that's what moves a project along.
    async reload() {
        const keepId = this.selectedTicket?.salesTicketId;

        if (!this.customer?.customerId) {
            this.clear();
            return;
        }

        this.loading = true;

        try {
            const all = (await this.salesTicketsService.getTicketsForCustomer(this.customer.customerId) || [])
                .filter(t => !t.isDeleted);

            this.tickets = all.filter(t => !t.isClosed);

            // Nothing in flight: show a project that just finished (won in the last 30 days) at 100%
            // rather than nothing at all, so the card doesn't vanish the moment a job is closed out.
            if (this.tickets.length === 0) {
                const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
                const recentWin = all
                    .filter(t => t.isClosed && t.closeResult === SALES_PIPELINE_STAGE_TYPES.won
                        && t.closedDate && new Date(t.closedDate).getTime() >= cutoff)
                    .sort((a, b) => new Date(b.closedDate).getTime() - new Date(a.closedDate).getTime())[0];

                if (recentWin)
                    this.tickets = [recentWin];
            }

            const keep = this.tickets.find(t => t.salesTicketId === keepId);
            await this.selectTicket(keep || this.tickets[0] || null);
        }
        catch {
            // Companies that haven't had the sales pipeline tables rolled out yet get a 500 here.
            // No project card is the right answer for them, not a broken one.
            this.clear();
        }
        finally {
            this.loading = false;
        }
    }

    async onTicketPicked(salesTicketId: number) {
        await this.selectTicket(this.tickets.find(t => t.salesTicketId === +salesTicketId) || null);
    }

    private clear() {
        this.tickets = [];
        this.selectedTicket = null;
        this.steps = [];
        this.rows = [];
        this.currentStep = null;
        this.nextStep = null;
        this.progressPercent = 0;
    }

    private async selectTicket(ticket: ISalesTicketModel) {
        this.selectedTicket = ticket;

        if (!ticket) {
            this.steps = [];
            this.rows = [];
            this.currentStep = null;
            this.nextStep = null;
            this.progressPercent = 0;
            return;
        }

        const pipeline = await this.getPipeline(ticket.salesPipelineId);
        this.steps = this.buildSteps(ticket, pipeline);
        this.currentStep = this.steps.find(s => s.state === 'current') || null;
        this.progressPercent = this.computeProgress(ticket, this.steps);
        this.nextStep = this.buildNextStep(ticket, this.steps);
        this.rows = this.buildRows(ticket, this.steps);
    }

    private async getPipeline(salesPipelineId: number): Promise<ISalesPipelineModel> {
        if (!this.pipelinesById.has(salesPipelineId))
            this.pipelinesById.set(salesPipelineId, await this.salesPipelinesService.getPipeline(salesPipelineId));

        return this.pipelinesById.get(salesPipelineId);
    }

    // ---- Timeline ------------------------------------------------------------

    private buildSteps(ticket: ISalesTicketModel, pipeline: ISalesPipelineModel): ProjectStep[] {
        // Only the Open columns are steps on the path; Won/Lost are how a ticket leaves the board.
        // The dedupe guards against a pipeline whose seed ran twice (seen on a dev database): of two
        // identical columns, keep the one the ticket actually references so the lookups below hit.
        const referenced = new Set([ticket.salesPipelineStageId, ticket.derivedStageId]);
        const byKey = new Map<string, ISalesPipelineStageModel>();

        for (const s of (pipeline?.stages || [])) {
            if (!s.isActive || (s.stageType || SALES_PIPELINE_STAGE_TYPES.open) !== SALES_PIPELINE_STAGE_TYPES.open)
                continue;

            const k = `${s.sortOrder}|${s.name}`;
            const existing = byKey.get(k);
            if (!existing || (referenced.has(s.salesPipelineStageId) && !referenced.has(existing.salesPipelineStageId)))
                byKey.set(k, s);
        }

        const stages = Array.from(byKey.values()).sort((a, b) => a.sortOrder - b.sortOrder);

        if (stages.length === 0)
            return [];

        // Where the ticket is: its own column, or if that isn't on the path (it's been hand-placed
        // somewhere inactive) the column its paperwork implies, or the first column.
        const placed = stages.find(s => s.salesPipelineStageId === ticket.salesPipelineStageId);
        const derived = stages.find(s => s.salesPipelineStageId === ticket.derivedStageId);
        let current = placed || derived || stages[0];

        // The same forward-only rule the board's auto-advance applies: paperwork that has moved on
        // since the card was last looked at pulls it forward, a manual drag back (which stamps
        // lastDerivedStatus) sticks. Doing it here too means the card is right even when the
        // ticket hasn't been through a board load since its last document.
        if (ticket.autoAdvance && placed && derived
            && ticket.derivedStatus !== ticket.lastDerivedStatus
            && derived.sortOrder > placed.sortOrder)
            current = derived;

        const isWon = ticket.isClosed && ticket.closeResult === SALES_PIPELINE_STAGE_TYPES.won;

        return stages.map(stage => {
            const state: StepState = isWon ? 'done'
                : stage.sortOrder < current.sortOrder ? 'done'
                : stage.sortOrder === current.sortOrder ? 'current'
                : 'todo';

            return {
                stageId: stage.salesPipelineStageId,
                name: stage.name,
                key: stage.derivedStageKey || null,
                sortOrder: stage.sortOrder,
                color: stage.color,
                icon: this.iconForKey(stage.derivedStageKey),
                state,
                date: state === 'todo' ? null : this.dateForStage(ticket, stage, state)
            };
        });
    }

    // The date each column was reached, read off the same ticket fields the board derives from.
    private dateForStage(ticket: ISalesTicketModel, stage: ISalesPipelineStageModel, state: StepState): Date {
        switch (stage.derivedStageKey) {
            case SALES_PIPELINE_STAGE_KEYS.lead:
                return ticket.createdDate;
            case SALES_PIPELINE_STAGE_KEYS.quote:
                return this.earliestDocumentDate(ticket, 'quote') || this.earliestDocumentDate(ticket, 'order') || ticket.createdDate;
            case SALES_PIPELINE_STAGE_KEYS.accepted:
                return ticket.quoteAcceptedDate || ticket.lathamOrderSubmittedDate;
            case SALES_PIPELINE_STAGE_KEYS.jobScheduled:
                return ticket.nextAppointmentDate;
            case SALES_PIPELINE_STAGE_KEYS.toBeBilled:
                return this.earliestDocumentDate(ticket, 'invoice');
            case SALES_PIPELINE_STAGE_KEYS.awaitingPayment:
                return ticket.invoiceDate;
            default:
                // A manual-only column has no paperwork event; the best we know is when the ticket
                // was last moved, and only if it's still sitting there.
                return state === 'current' ? ticket.statusChangedDate : null;
        }
    }

    private earliestDocumentDate(ticket: ISalesTicketModel, documentType: string): Date {
        const dates = (ticket.documents || [])
            .filter(d => d.documentType === documentType && d.documentDate)
            .map(d => new Date(d.documentDate).getTime());

        return dates.length ? new Date(Math.min(...dates)) : null;
    }

    private iconForKey(key: string): string {
        switch (key) {
            case SALES_PIPELINE_STAGE_KEYS.lead: return 'bi-people-fill';
            case SALES_PIPELINE_STAGE_KEYS.quote: return 'bi-file-earmark-text-fill';
            case SALES_PIPELINE_STAGE_KEYS.accepted: return 'bi-check-lg';
            case SALES_PIPELINE_STAGE_KEYS.jobScheduled: return 'bi-calendar-check-fill';
            case SALES_PIPELINE_STAGE_KEYS.toBeBilled: return 'bi-receipt';
            case SALES_PIPELINE_STAGE_KEYS.awaitingPayment: return 'bi-cash-stack';
            default: return 'bi-flag-fill';
        }
    }

    // Steps reached over the steps on the path plus one for Complete, so Awaiting Payment isn't
    // already "100%" while the money is still out. A won ticket is complete.
    private computeProgress(ticket: ISalesTicketModel, steps: ProjectStep[]): number {
        if (ticket.isClosed && ticket.closeResult === SALES_PIPELINE_STAGE_TYPES.won)
            return 100;

        if (steps.length === 0)
            return 0;

        const reached = steps.filter(s => s.state !== 'todo').length;
        return Math.round(reached / (steps.length + 1) * 100);
    }

    get ringOffset(): number {
        return this.ringCircumference * (1 - this.progressPercent / 100);
    }

    // ---- Next step -------------------------------------------------------------
    // What has to happen for the ticket to move to the following column, and the button that does
    // it. Keyed on the *next* column's derived key, since that's what the board will be waiting for.

    private buildNextStep(ticket: ISalesTicketModel, steps: ProjectStep[]): NextStep {
        if (ticket.isClosed)
            return { text: `Project closed as ${ticket.closeResult || 'done'}`, buttonLabel: null, buttonIcon: null, action: null };

        const currentIdx = steps.findIndex(s => s.state === 'current');
        const next = currentIdx >= 0 ? steps[currentIdx + 1] : null;
        const ticketId = ticket.salesTicketId;

        if (!next) {
            // Past the last column: only the money is outstanding
            if (ticket.isPaid)
                return { text: 'Paid in full. Close the project as Won on the pipeline board.', buttonLabel: null, buttonIcon: null, action: null };

            return {
                text: 'Collect payment',
                buttonLabel: this.paymentsAuthService.canAdd() ? 'PAYMENT' : null,
                buttonIcon: 'bi-plus-lg',
                action: () => this.createPayment.emit()
            };
        }

        const firstQuote = this.firstDocument(ticket, 'quote');
        const firstJob = this.firstDocument(ticket, 'job');
        const firstInvoice = this.firstDocument(ticket, 'invoice');

        switch (next.key) {
            case SALES_PIPELINE_STAGE_KEYS.quote:
                return {
                    text: 'Send the customer a quote',
                    buttonLabel: this.estimatesAuthService.canAdd() ? 'QUOTE' : null,
                    buttonIcon: 'bi-plus-lg',
                    action: () => this.createQuote.emit(ticketId)
                };

            case SALES_PIPELINE_STAGE_KEYS.accepted:
                return {
                    text: 'Get the quote accepted',
                    buttonLabel: firstQuote ? 'OPEN QUOTE' : null,
                    buttonIcon: 'bi-box-arrow-up-right',
                    action: () => this.openQuote.emit(firstQuote.documentId)
                };

            case SALES_PIPELINE_STAGE_KEYS.jobScheduled:
                if (firstJob)
                    return {
                        text: 'Put an appointment on the job',
                        buttonLabel: 'OPEN JOB',
                        buttonIcon: 'bi-box-arrow-up-right',
                        action: () => this.openJob.emit(firstJob.documentId)
                    };

                return {
                    text: 'Schedule the job',
                    buttonLabel: this.jobsAuthService.canAdd() ? 'JOB' : null,
                    buttonIcon: 'bi-plus-lg',
                    action: () => this.createJob.emit(ticketId)
                };

            case SALES_PIPELINE_STAGE_KEYS.toBeBilled:
                return {
                    text: 'Create the invoice',
                    buttonLabel: this.invoicesAuthService.canAdd() ? 'INVOICE' : null,
                    buttonIcon: 'bi-plus-lg',
                    action: () => this.createInvoice.emit(ticketId)
                };

            case SALES_PIPELINE_STAGE_KEYS.awaitingPayment:
                return {
                    text: 'Date and send the invoice',
                    buttonLabel: firstInvoice ? 'OPEN INVOICE' : null,
                    buttonIcon: 'bi-box-arrow-up-right',
                    action: () => this.openInvoice.emit(firstInvoice.documentId)
                };

            default:
                // A manual-only column: somebody drags the card there when it's time
                return { text: `Move to ${next.name} on the pipeline board`, buttonLabel: null, buttonIcon: null, action: null };
        }
    }

    private firstDocument(ticket: ISalesTicketModel, documentType: string): ISalesTicketDocumentModel {
        return (ticket.documents || []).find(d => d.documentType === documentType) || null;
    }

    // ---- Related documents -------------------------------------------------------

    private buildRows(ticket: ISalesTicketModel, steps: ProjectStep[]): ProjectDocumentRow[] {
        const rows: ProjectDocumentRow[] = [];
        const documents = ticket.documents || [];
        const byDateAsc = (a: ISalesTicketDocumentModel, b: ISalesTicketDocumentModel) =>
            new Date(a.documentDate || 0).getTime() - new Date(b.documentDate || 0).getTime();

        const push = (step: ProjectStep, items: Partial<ProjectDocumentRow>[]) => {
            if (items.length === 0)
                items = [{}];

            items.forEach((item, idx) => rows.push({
                stepName: step.name,
                icon: step.icon,
                state: step.state,
                date: item.date || null,
                activity: item.activity || '',
                documentLabel: item.documentLabel || '',
                documentType: item.documentType || null,
                documentId: item.documentId || 0,
                first: idx === 0
            }));
        };

        for (const step of steps) {
            switch (step.key) {
                case SALES_PIPELINE_STAGE_KEYS.lead:
                    push(step, [{
                        date: ticket.createdDate,
                        activity: 'New lead created',
                        documentLabel: ticket.ticketNumber,
                        documentType: 'ticket',
                        documentId: ticket.salesTicketId
                    }]);
                    break;

                case SALES_PIPELINE_STAGE_KEYS.quote:
                    push(step, documents
                        .filter(d => d.documentType === 'quote' || d.documentType === 'order')
                        .sort(byDateAsc)
                        .map(d => d.documentType === 'quote'
                            ? { date: d.documentDate, activity: `Quote #${d.label} created`, documentLabel: `Quote #${d.label}`, documentType: 'quote' as const, documentId: d.documentId }
                            : { date: d.documentDate, activity: `Latham order ${d.label} created`, documentLabel: `Order ${d.label}`, documentType: 'order' as const, documentId: d.documentId }));
                    break;

                case SALES_PIPELINE_STAGE_KEYS.accepted: {
                    const accepted = documents
                        .filter(d => (d.documentType === 'quote' && d.statusLabel === 'Accepted')
                            || (d.documentType === 'order' && d.statusLabel !== 'Not submitted'))
                        .sort(byDateAsc)
                        .map(d => d.documentType === 'quote'
                            ? { date: ticket.quoteAcceptedDate || d.documentDate, activity: `Quote #${d.label} accepted`, documentLabel: `Quote #${d.label}`, documentType: 'quote' as const, documentId: d.documentId }
                            : { date: ticket.lathamOrderSubmittedDate || d.documentDate, activity: `Latham order ${d.label} submitted`, documentLabel: `Order ${d.label}`, documentType: 'order' as const, documentId: d.documentId });
                    push(step, accepted);
                    break;
                }

                case SALES_PIPELINE_STAGE_KEYS.jobScheduled:
                    push(step, documents
                        .filter(d => d.documentType === 'job')
                        .sort(byDateAsc)
                        .map(d => ({
                            date: d.documentDate,
                            activity: ticket.nextAppointmentDate ? `Job ${d.label} scheduled` : `Job ${d.label} created, not yet scheduled`,
                            documentLabel: `Job ${d.label}`,
                            documentType: 'job' as const,
                            documentId: d.documentId
                        })));
                    break;

                case SALES_PIPELINE_STAGE_KEYS.toBeBilled:
                    push(step, documents
                        .filter(d => d.documentType === 'invoice' && !d.documentDate)
                        .map(d => ({ date: null, activity: `Invoice #${d.label} created`, documentLabel: `Invoice #${d.label}`, documentType: 'invoice' as const, documentId: d.documentId })));
                    break;

                case SALES_PIPELINE_STAGE_KEYS.awaitingPayment:
                    push(step, documents
                        .filter(d => d.documentType === 'invoice' && !!d.documentDate)
                        .sort(byDateAsc)
                        .map(d => ({ date: d.documentDate, activity: `Invoice #${d.label} issued`, documentLabel: `Invoice #${d.label}`, documentType: 'invoice' as const, documentId: d.documentId })));
                    break;

                default:
                    push(step, step.state === 'todo' ? [] : [{ date: step.date, activity: `Moved to ${step.name}` }]);
            }
        }

        // The finish line. A won ticket is complete; anything else is still on its way.
        const complete: ProjectStep = {
            stageId: 0, name: 'Complete', key: null, sortOrder: Number.MAX_SAFE_INTEGER, color: null,
            icon: 'bi-trophy-fill',
            state: ticket.isClosed && ticket.closeResult === SALES_PIPELINE_STAGE_TYPES.won ? 'done' : 'todo',
            date: ticket.closedDate
        };
        push(complete, complete.state === 'done'
            ? [{ date: ticket.closedDate, activity: 'Project closed as Won' }]
            : []);

        return rows;
    }

    onRowClicked(row: ProjectDocumentRow) {
        if (!row.documentId)
            return;

        switch (row.documentType) {
            case 'ticket': this.goToProjectDetail(); break;
            case 'quote': this.openQuote.emit(row.documentId); break;
            case 'order': this.openLathamOrder.emit(row.documentId); break;
            case 'job': this.openJob.emit(row.documentId); break;
            case 'invoice': this.openInvoice.emit(row.documentId); break;
        }
    }

    // The board opens the ticket's detail dialog when it's told which one to show
    goToProjectDetail() {
        if (!this.canOpenBoard || !this.selectedTicket)
            return;

        this.router.navigate(['/sales-pipeline'], { queryParams: { salesTicketId: this.selectedTicket.salesTicketId } });
    }
}
